/**
 * Export service — builds a printable model of a Frostgrave warband or
 * Stargrave crew, and renders it as Markdown (download) or standalone
 * print-friendly HTML (browser print dialog → Save as PDF).
 */

import { loadWarbandData, StoredMember } from './localStorageService';
import { useReferenceDataStore } from '../stores/referenceDataStore';
import { warbandMemberImageService } from './warbandMemberImageService';
import { getCaptainLevel, getCaptainSharePercent } from '../utils/captainRules';
import { computeLevelUpStatBonuses, formatSoldierType } from '../utils/formatting';
import { getSplitStatValue } from '../utils/splitStats';
import { stargraveBackgrounds, stargravePowers } from '../data/stargrave/coreRules';
import { stargraveShipUpgrades } from '../data/stargrave/shipUpgrades';
import { stargraveAdvancedTechnology } from '../data/stargrave/advancedTechnology';
import { stargraveAdvancedWeapons } from '../data/stargrave/advancedWeapons';
import { stargraveAlienArtefacts } from '../data/stargrave/alienArtefacts';
import { stargravePermanentInjuries } from '../data/stargrave/permanentInjuries';
import { getAllStargraveEquipment, getAllStargraveSoldiers } from '../stores/stargraveCustomStore';
import { getEffectiveLeaderStats } from '../game/stargraveCampaignRules';
import { applyStargraveRewireModifiers } from '../game/stargraveSoldierRules';
import { applyStargraveGearModifiers } from '../game/stargraveLeaderRules';
import { applyStargraveTechnologyModifiers } from '../game/stargraveTechRules';
import { applyStargraveBuffsToStats } from '../game/stargraveBuffRules';
import { applyStargraveLootPenalties } from '../game/stargravePlayRules';
import { getStargraveUpkeepSuits } from '../game/stargraveUpkeepRules';
import { BuffOrDebuff, StargraveCrewData, StargraveLeader, StargraveStatLine, WarbandMemberDTO } from '../types';

// ─── Model ───────────────────────────────────────────────────────────────────

export interface ExportMember {
  memberId: string;
  name: string;
  subtitle: string;
  /** e.g. [['M','6'],['F','+2'],…] */
  stats: Array<[string, string]>;
  /** Labelled detail lines, e.g. Equipment, Injuries, Notes. */
  details: Array<{ label: string; value: string }>;
}

export interface ExportCard {
  title: string;
  meta?: string;
  body?: string;
}

export interface ExportSection {
  title: string;
  paragraphs?: string[];
  bullets?: string[];
  table?: { headers: string[]; rows: string[][] };
  members?: ExportMember[];
  /** Card-styled blocks (powers, cargo, ship upgrades) printed in a grid. */
  cards?: ExportCard[];
  /** Labelled card groups printed within the same section/page. */
  cardGroups?: Array<{ label: string; cards: ExportCard[] }>;
}

export interface ExportModel {
  title: string;
  subtitle?: string;
  facts: Array<[string, string]>;
  sections: ExportSection[];
}

const signed = (value: number) => (value >= 0 ? `+${value}` : `${value}`);

/** "base/adjusted" when modifiers apply, plain value otherwise. */
const splitStat = (base: number, adjusted: number, sign = false) => {
  const format = sign ? signed : (value: number) => `${value}`;
  return base === adjusted ? format(adjusted) : `${format(base)}/${format(adjusted)}`;
};

const statPairs = (base: StargraveStatLine, adjusted: StargraveStatLine): Array<[string, string]> => [
  ['M', splitStat(base.move, adjusted.move)],
  ['F', splitStat(base.fight, adjusted.fight, true)],
  ['S', splitStat(base.shoot, adjusted.shoot, true)],
  ['A', splitStat(base.armour, adjusted.armour)],
  ['W', splitStat(base.will, adjusted.will, true)],
  ['H', splitStat(base.health, adjusted.health)],
];

// ─── Frostgrave ──────────────────────────────────────────────────────────────

type StatRecord = Record<string, number | undefined>;

function frostgraveMember(
  stored: StoredMember,
  subtitle: string,
  warbandId: string,
  warbandBuffs: BuffOrDebuff[],
  levelUpBonuses: { fight: number; shoot: number; will: number; health: number }
): ExportMember {
  const ref = useReferenceDataStore.getState();
  const stats = stored as unknown as StatRecord;
  const names = (ids: string[], lookup: Array<{ id: string; name: string }>) =>
    ids.map((id) => lookup.find((item) => item.id === id)?.name ?? id).join(', ');
  const injuries = (stored.permanentInjuryIds ?? [])
    .map((id) => ref.injuries.find((injury) => injury.id === id)?.name ?? id).join(', ');
  const buffCounts = new Map<string, number>();
  for (const id of stored.buffIds) buffCounts.set(id, (buffCounts.get(id) ?? 0) + 1);
  // Hydrated DTO so stats are computed exactly as in play mode.
  const dto: WarbandMemberDTO = {
    id: stored.id,
    creatureId: stored.creatureId,
    warbandId,
    name: stored.name,
    currentHealth: stored.currentHealth,
    notes: stored.notes,
    isTemporary: stored.isTemporary,
    move: stats.move ?? 6, fight: stats.fight ?? 0, shoot: stats.shoot ?? 0,
    armour: stats.armour ?? 10, will: stats.will ?? 0, health: stats.health ?? 10,
    itemSlots: stats.itemSlots ?? 5,
    creatureName: (stored as unknown as Record<string, string>).creatureName ?? stored.name,
    leftTable: stored.leftTable,
    carryingTreasure: stored.carryingTreasure,
    poisoned: stored.poisoned,
    storedInInn: stored.storedInInn,
    buffsAndDebuffs: Array.from(buffCounts.entries()).flatMap(([id, count]) => {
      const buff = ref.buffs.find((candidate) => candidate.id === id);
      return buff ? [{ ...buff, stackCount: count }] : [];
    }),
    permanentInjuries: (stored.permanentInjuryIds ?? []).flatMap((id) => {
      const injury = ref.injuries.find((candidate) => candidate.id === id);
      return injury ? [injury] : [];
    }),
    weapons: stored.weaponIds.flatMap((id) => ref.weapons.find((weapon) => weapon.id === id) ?? []),
    armours: stored.armourIds.flatMap((id) => ref.armours.find((armour) => armour.id === id) ?? []),
    treasureItems: stored.treasureItemIds.flatMap((id) => ref.treasureItems.find((item) => item.id === id) ?? []),
  };
  const adjusted = (stat: 'move' | 'fight' | 'shoot' | 'armour' | 'will' | 'health', bonus = 0) =>
    getSplitStatValue(dto, stat, dto[stat] + bonus, warbandBuffs);
  const maxHealth = adjusted('health', levelUpBonuses.health);
  const details: Array<{ label: string; value: string }> = [
    { label: 'Health', value: `${Math.min(stored.currentHealth, maxHealth)} / ${maxHealth}` },
  ];
  if (stored.weaponIds.length + stored.armourIds.length > 0) {
    details.push({ label: 'Equipment', value: [names(stored.weaponIds, ref.weapons), names(stored.armourIds, ref.armours)].filter(Boolean).join(', ') });
  }
  if (stored.treasureItemIds.length > 0) details.push({ label: 'Items', value: names(stored.treasureItemIds, ref.treasureItems) });
  if (injuries) details.push({ label: 'Injuries', value: injuries });
  if (stored.storedInInn) details.push({ label: 'Status', value: 'Stored in Inn' });
  if (stored.notes) details.push({ label: 'Notes', value: stored.notes });
  return {
    memberId: stored.id,
    name: stored.name,
    subtitle,
    stats: [
      ['M', splitStat(dto.move, adjusted('move'))],
      ['F', splitStat(dto.fight + levelUpBonuses.fight, adjusted('fight', levelUpBonuses.fight), true)],
      ['S', splitStat(dto.shoot + levelUpBonuses.shoot, adjusted('shoot', levelUpBonuses.shoot), true)],
      ['A', splitStat(dto.armour, adjusted('armour'))],
      ['W', splitStat(dto.will + levelUpBonuses.will, adjusted('will', levelUpBonuses.will), true)],
      ['H', splitStat(dto.health + levelUpBonuses.health, maxHealth)],
    ],
    details,
  };
}

export interface ExportOptions {
  /** Adds a Pre-Game page before the roster and Post-Game / Experience Tracker pages at the end. */
  includeGamePages?: boolean;
}

/** Blank tally sheet for one game's experience awards. */
const experienceTrackerSection = (rows: Array<[string, string]>): ExportSection => ({
  title: 'Experience Tracker',
  paragraphs: ['Tally awards during one game, then add the total to your experience afterwards.'],
  table: {
    headers: ['Award', 'XP', 'Tally', 'Total'],
    rows: [...rows.map(([award, xp]): string[] => [award, xp, '', '']), ['Game total', '', '', '']],
  },
});

export async function buildFrostgraveExport(warbandId: string, options: ExportOptions = {}): Promise<ExportModel> {
  const stored = loadWarbandData(warbandId);
  if (!stored) throw new Error('Warband not found.');
  const ref = useReferenceDataStore.getState();
  const { warband } = stored;

  const facts: Array<[string, string]> = [
    ['Gold', `${warband.gold}gc`],
    ['Experience', `${warband.experience} XP`],
  ];
  const school = ref.getSchoolNameById(warband.schoolId);
  if (school) facts.push(['School', school]);
  const base = ref.getBaseTypeById(warband.baseId);
  if (base) facts.push(['Base', base.name]);

  const members: ExportMember[] = [];
  const warbandBuffs = stored.warbandBuffIds.flatMap((id) => {
    const buff = ref.buffs.find((candidate) => candidate.id === id);
    return buff ? [buff] : [];
  });
  const levelUpBonuses = computeLevelUpStatBonuses(stored.levelUps);
  const member = (storedMember: StoredMember, subtitle: string) =>
    frostgraveMember(storedMember, subtitle, warbandId, warbandBuffs, levelUpBonuses);
  if (stored.wizard) members.push(member(stored.wizard, 'Wizard'));
  if (stored.apprentice) members.push(member(stored.apprentice, 'Apprentice'));
  if (stored.captain) {
    const level = getCaptainLevel(stored.captain.advances);
    const captain = member(stored.captain, `Captain — Level ${level}`);
    captain.details.unshift({
      label: 'Captain',
      value: `${stored.captain.experience} XP, ${getCaptainSharePercent(level)}% share`
        + (stored.captain.trickIds.length > 0
          ? `. Tricks: ${stored.captain.trickIds.map((id) => ref.tricks.find((trick) => trick.id === id)?.name ?? id).join(', ')}`
          : ''),
    });
    members.push(captain);
  }
  for (const soldier of stored.soldiers) {
    members.push(member(soldier, formatSoldierType(soldier.type, true)));
  }

  const sections: ExportSection[] = [{ title: 'Roster', members }];

  // Spells with warband-adjusted casting numbers
  const { spellService } = await import('./spellService');
  const spells = (await spellService.getAllForWarband(warbandId)).data ?? [];
  const spellCards = (categoryId: string): ExportCard[] => spells
    .filter((spell) => spell.categories.some((category) => category.id === categoryId))
    .map((spell) => ({
      title: spell.name,
      meta: `${spell.school} · CN ${spell.castingNumber}`,
      body: spell.description ?? undefined,
    }));
  if (options.includeGamePages) {
    const preGameCards = spellCards('out_of_game_b');
    sections.unshift({
      title: 'Pre-Game',
      ...(preGameCards.length > 0
        ? { cardGroups: [{ label: 'Out of Game (B) Spells', cards: preGameCards }] }
        : { paragraphs: ['No Out of Game (B) spells are known.'] }),
    });
  }
  // Spell cards listed alphabetically.
  const spellCardList: ExportCard[] = [...spells]
    .sort((left, right) => left.name.localeCompare(right.name))
    .map((spell) => ({
      title: spell.name,
      meta: `${spell.school} · CN ${spell.castingNumber}`,
      body: spell.description ?? undefined,
    }));
  if (spellCardList.length > 0) sections.push({ title: 'Spell Cards', cards: spellCardList });

  // Base (resource cards) and Vault share a section, mirroring Ship & Cargo.
  const memberName = (memberId: string | null) =>
    [stored.wizard, stored.apprentice, stored.captain, ...stored.soldiers]
      .find((member) => member?.id === memberId)?.name;
  const resourceCards: ExportCard[] = stored.selectedBaseResourceIds.map((id) => {
    const resource = ref.allBaseResources.find((candidate) => candidate.id === id);
    return {
      title: resource?.name ?? id,
      meta: 'Base Resource',
      body: resource?.description,
    };
  });
  const vaultCards: ExportCard[] = stored.vaultItems.map((item) => {
    const treasure = ref.treasureItems.find((candidate) => candidate.id === item.itemId);
    const holder = memberName(item.warbandMemberId);
    return {
      title: treasure?.name ?? item.itemId,
      meta: `Magic Item · ${holder ? `Carried by ${holder}` : 'In vault'}`,
      body: treasure?.description ?? undefined,
    };
  });
  if (base || resourceCards.length > 0 || vaultCards.length > 0) {
    sections.push({
      title: base ? `Base — ${base.name}` : 'Base & Vault',
      ...(resourceCards.length === 0 && vaultCards.length === 0
        ? { paragraphs: ['No base resources or vault items.'] }
        : {}),
      cards: resourceCards,
      ...(vaultCards.length > 0 ? { cardGroups: [{ label: 'Vault', cards: vaultCards }] } : {}),
    });
  }

  if (warband.description) sections.push({ title: 'Notes', paragraphs: [warband.description] });

  if (options.includeGamePages) {
    const postGameCards = spellCards('out_of_game_a');
    sections.push({
      title: 'Post-Game',
      ...(postGameCards.length > 0
        ? { cardGroups: [{ label: 'Out of Game (A) Spells', cards: postGameCards }] }
        : { paragraphs: ['No Out of Game (A) spells are known.'] }),
    });
    sections.push(experienceTrackerSection([
      ['Treasure token secured', '20 each'],
      ['Spell successfully cast', '10 each'],
      ['Uncontrolled creature killed', '5 each'],
      ['Scenario / bonus XP', 'varies'],
    ]));
  }

  return { title: warband.name, subtitle: 'Frostgrave Warband', facts, sections };
}

// ─── Stargrave ───────────────────────────────────────────────────────────────

/** Gear IDs excluding base weapons replaced by owned Advanced Weapons (play-mode rule). */
function visibleGearIds(crew: StargraveCrewData, ownerId: string, gearIds: string[]): string[] {
  const replaced = new Set(crew.advancedWeapons
    .filter((instance) => instance.ownerId === ownerId)
    .map((instance) => stargraveAdvancedWeapons.find((weapon) => weapon.id === instance.weaponId)?.baseWeaponId)
    .filter((id): id is string => Boolean(id)));
  return gearIds.filter((id) => !replaced.has(id));
}

/** Full play-mode stat pipeline: gear → technology → buffs → loot. */
function stargravePlayStats(
  crew: StargraveCrewData,
  memberId: string,
  baseStats: StargraveStatLine,
  gearModifierIds: string[],
  gameState: { activeBuffs: Parameters<typeof applyStargraveBuffsToStats>[1]; loot: Parameters<typeof applyStargraveLootPenalties>[1] }
): StargraveStatLine {
  const gearStats = applyStargraveGearModifiers(baseStats, gearModifierIds);
  const techStats = applyStargraveTechnologyModifiers(gearStats, crew.advancedTechnology, memberId);
  const buffedStats = applyStargraveBuffsToStats(techStats, gameState.activeBuffs);
  return applyStargraveLootPenalties(buffedStats, gameState.loot);
}

function stargraveLeaderMember(crew: StargraveCrewData, leader: StargraveLeader): ExportMember {
  const equipment = getAllStargraveEquipment();
  const background = stargraveBackgrounds.find((candidate) => candidate.id === leader.backgroundId);
  const details: Array<{ label: string; value: string }> = [];
  if (leader.gearIds.length > 0) {
    details.push({ label: 'Gear', value: leader.gearIds.map((id) => equipment.find((item) => item.id === id)?.name ?? id).join(', ') });
  }
  if (leader.powers.length > 0) {
    details.push({
      label: 'Powers',
      value: leader.powers.map((selection) => {
        const power = stargravePowers.find((candidate) => candidate.id === selection.powerId);
        return `${power?.name ?? selection.powerId} (${selection.activation}/${selection.strain})`;
      }).join(', '),
    });
  }
  if (leader.permanentInjuryIds.length > 0) {
    details.push({
      label: 'Injuries',
      value: leader.permanentInjuryIds.map((id) =>
        stargravePermanentInjuries.find((injury) => injury.id === id)?.name ?? id).join(', '),
    });
  }
  if (leader.notes) details.push({ label: 'Notes', value: leader.notes });
  const stats = stargravePlayStats(
    crew, leader.id, getEffectiveLeaderStats(leader),
    visibleGearIds(crew, leader.id, leader.gearIds), leader.gameState);
  details.unshift({ label: 'Health', value: `${Math.min(leader.gameState.currentHealth, stats.health)} / ${stats.health}` });
  return {
    memberId: leader.id,
    name: leader.name,
    subtitle: `${leader.role === 'captain' ? 'Captain' : 'First Mate'} — ${background?.name ?? 'Leader'}, Level ${leader.level}`,
    stats: statPairs(leader.stats, stats),
    details,
  };
}

export function buildStargraveExport(warbandId: string, options: ExportOptions = {}): ExportModel {
  const stored = loadWarbandData(warbandId);
  if (!stored?.stargrave) throw new Error('Crew not found.');
  const { warband, stargrave: crew } = stored;
  const equipment = getAllStargraveEquipment();
  const profiles = getAllStargraveSoldiers();

  const facts: Array<[string, string]> = [
    ['Credits', `${warband.gold}cr`],
    ['Experience', `${warband.experience} XP`],
  ];
  if (crew.secrets > 0) facts.push(['Secrets', `${crew.secrets}`]);

  const memberName = (memberId: string | null) =>
    [crew.captain, crew.firstMate, ...crew.soldiers].find((member) => member?.id === memberId)?.name;

  const members: ExportMember[] = [];
  if (crew.captain) members.push(stargraveLeaderMember(crew, crew.captain));
  if (crew.firstMate) members.push(stargraveLeaderMember(crew, crew.firstMate));
  for (const soldier of crew.soldiers) {
    const profile = profiles.find((candidate) => candidate.id === soldier.profileId);
    const profileStats = profile ?? { move: 0, fight: 0, shoot: 0, armour: 0, will: 0, health: 0 };
    const baseStats = applyStargraveRewireModifiers(profileStats, soldier.rewiredStat);
    // Soldier profile stat lines already include their standard equipment.
    const stats = stargravePlayStats(crew, soldier.id, baseStats, [], soldier.gameState);
    const flags = [
      soldier.isRobot ? 'Robot' : null,
      soldier.rewiredStat ? `Re-wired (+1 ${soldier.rewiredStat})` : null,
      soldier.storedInShip ? 'Stored in Ship' : null,
      soldier.isTemporary ? 'Temporary' : null,
    ].filter((flag): flag is string => Boolean(flag));
    const details: Array<{ label: string; value: string }> = [
      { label: 'Health', value: `${Math.min(soldier.gameState.currentHealth, stats.health)} / ${stats.health}` },
    ];
    if (profile && profile.equipmentIds.length > 0) {
      details.push({ label: 'Equipment', value: profile.equipmentIds.map((id) => equipment.find((item) => item.id === id)?.name ?? id).join(', ') });
    }
    if (flags.length > 0) details.push({ label: 'Status', value: flags.join(', ') });
    if (soldier.notes) details.push({ label: 'Notes', value: soldier.notes });
    members.push({
      memberId: soldier.id,
      name: soldier.name,
      subtitle: `${profile?.name ?? 'Soldier'} (${profile ? formatStargraveCategory(profile.category) : 'Standard'})`,
      stats: statPairs(profileStats, stats),
      details,
    });
  }

  const sections: ExportSection[] = [{ title: 'Roster', members }];

  const leaderPowerGroups = (
    label: (leaderName: string) => string,
    filter: (timing: string) => boolean
  ) => [crew.captain, crew.firstMate]
    .filter((leader): leader is StargraveLeader => Boolean(leader))
    .map((leader) => ({
      label: label(leader.name),
      cards: leader.powers.flatMap((selection) => {
        const power = stargravePowers.find((candidate) => candidate.id === selection.powerId);
        if (!power || !filter(power.timing)) return [];
        return [{
          title: power.name,
          meta: `Activation ${selection.activation} · Strain ${selection.strain} · ${power.timing}`,
          body: power.summary,
        }];
      }),
    }))
    .filter((group) => group.cards.length > 0);

  if (options.includeGamePages) {
    const preGameGroups = leaderPowerGroups(
      (name) => `Out of Game (B) Powers — ${name}`,
      (timing) => timing.includes('Out of Game (B)'));
    const upkeepSuits = getStargraveUpkeepSuits(crew);
    sections.unshift({
      title: 'Pre-Game',
      ...(upkeepSuits.length > 0
        ? { bullets: upkeepSuits.map((suit) => `Combat Armour Upkeep: ${suit.ownerName} — ${suit.itemName} (${suit.cost}cr)`) }
        : {}),
      ...(preGameGroups.length > 0
        ? { cardGroups: preGameGroups }
        : { paragraphs: ['No Out of Game (B) powers are known.'] }),
    });
  }

  // With game pages enabled, the main powers section shows only in-game powers.
  const powerGroups = leaderPowerGroups(
    (name) => name,
    (timing) => !options.includeGamePages || !timing.startsWith('Out of Game'));
  if (powerGroups.length > 0) sections.push({ title: 'Power Cards', cardGroups: powerGroups });

  const cargoCards: ExportCard[] = [
    ...crew.advancedTechnology.map((instance) => {
      const item = stargraveAdvancedTechnology.find((candidate) => candidate.id === instance.technologyId);
      const owner = memberName(instance.ownerId);
      const uses = instance.remainingUses !== undefined ? ` · ${instance.remainingUses} uses` : '';
      return {
        title: item?.name ?? instance.technologyId,
        meta: `Advanced Technology · ${owner ? `Assigned to ${owner}` : 'In cargo'}${uses}`,
        body: [item?.summary, item?.restriction].filter(Boolean).join(' '),
      };
    }),
    ...crew.advancedWeapons.map((instance) => {
      const item = stargraveAdvancedWeapons.find((candidate) => candidate.id === instance.weaponId);
      const owner = memberName(instance.ownerId);
      return {
        title: item?.name ?? instance.weaponId,
        meta: `Advanced Weapon · ${owner ? `Assigned to ${owner}` : 'In cargo'}`,
        body: item?.summary,
      };
    }),
    ...crew.alienArtefacts.map((instance) => {
      const item = stargraveAlienArtefacts.find((candidate) => candidate.id === instance.artefactId);
      const owner = memberName(instance.ownerId);
      const uses = instance.remainingUses !== undefined ? ` · ${instance.remainingUses} uses` : '';
      return {
        title: item?.name ?? instance.artefactId,
        meta: `Alien Artefact · ${owner ? `Assigned to ${owner}` : 'In cargo'}${uses}`,
        body: item?.summary,
      };
    }),
  ];

  // Ship (upgrades first) and cargo share a section, and so a printed page.
  const upgradeCounts = new Map<string, number>();
  for (const id of crew.ship.upgradeIds) upgradeCounts.set(id, (upgradeCounts.get(id) ?? 0) + 1);
  sections.push({
    title: `Ship — ${crew.ship.name}`,
    ...(upgradeCounts.size === 0 ? { paragraphs: ['No upgrades installed.'] } : {}),
    cards: Array.from(upgradeCounts.entries()).map(([id, count]) => {
      const upgrade = stargraveShipUpgrades.find((candidate) => candidate.id === id);
      return {
        title: `${upgrade?.name ?? id}${count > 1 ? ` ×${count}` : ''}`,
        meta: 'Ship Upgrade',
        body: upgrade?.summary,
      };
    }),
    ...(cargoCards.length > 0 ? { cardGroups: [{ label: 'Cargo', cards: cargoCards }] } : {}),
  });

  if (warband.description) sections.push({ title: 'Notes', paragraphs: [warband.description] });

  if (options.includeGamePages) {
    const postGameGroups = leaderPowerGroups(
      (name) => `Out of Game (A) Powers — ${name}`,
      (timing) => timing.includes('Out of Game (A)'));
    sections.push({
      title: 'Post-Game',
      ...(postGameGroups.length > 0
        ? { cardGroups: postGameGroups }
        : { paragraphs: ['No Out of Game (A) powers are known.'] }),
    });
    sections.push(experienceTrackerSection([
      ['Game played', '30'],
      ['Friendly crew member knocked out', '30 (once)'],
      ['Loot token unlocked', '20 each'],
      ['Power activated', '10 each (max 100)'],
      ['Rival crew knockout', '10 each (max 40)'],
      ['Creature knockout', '5 each (max 20)'],
      ['Scenario bonus', 'varies'],
    ]));
  }

  return { title: warband.name, subtitle: 'Stargrave Crew', facts, sections };
}

const formatStargraveCategory = (category: 'standard' | 'specialist' | 'creature') =>
  category === 'specialist' ? 'Specialist' : category === 'creature' ? 'Creature' : 'Standard';

export async function buildWarbandExport(warbandId: string, options: ExportOptions = {}): Promise<ExportModel> {
  const stored = loadWarbandData(warbandId);
  if (!stored) throw new Error('Warband not found.');
  return stored.warband.gameSystem === 'stargrave'
    ? buildStargraveExport(warbandId, options)
    : buildFrostgraveExport(warbandId, options);
}

// ─── Markdown renderer ───────────────────────────────────────────────────────

export function renderExportMarkdown(model: ExportModel): string {
  const lines: string[] = [`# ${model.title}`];
  if (model.subtitle) lines.push('', `*${model.subtitle}*`);
  if (model.facts.length > 0) lines.push('', model.facts.map(([label, value]) => `**${label}:** ${value}`).join(' · '));
  for (const section of model.sections) {
    lines.push('', `## ${section.title}`);
    for (const paragraph of section.paragraphs ?? []) lines.push('', paragraph);
    if (section.bullets && section.bullets.length > 0) {
      lines.push('', ...section.bullets.map((bullet) => `- ${bullet}`));
    }
    if (section.table) {
      lines.push('', `| ${section.table.headers.join(' | ')} |`);
      lines.push(`| ${section.table.headers.map(() => '---').join(' | ')} |`);
      for (const row of section.table.rows) lines.push(`| ${row.join(' | ')} |`);
    }
    for (const member of section.members ?? []) {
      lines.push('', `### ${member.name}`, '', `*${member.subtitle}*`, '');
      lines.push(`| ${member.stats.map(([label]) => label).join(' | ')} |`);
      lines.push(`| ${member.stats.map(() => '---').join(' | ')} |`);
      lines.push(`| ${member.stats.map(([, value]) => value).join(' | ')} |`);
      for (const detail of member.details) lines.push('', `- **${detail.label}:** ${detail.value}`);
    }
    for (const card of section.cards ?? []) {
      lines.push('', `### ${card.title}`);
      if (card.meta) lines.push('', `*${card.meta}*`);
      if (card.body) lines.push('', card.body);
    }
    for (const group of section.cardGroups ?? []) {
      lines.push('', `### ${group.label}`);
      for (const card of group.cards) {
        lines.push('', `#### ${card.title}`);
        if (card.meta) lines.push('', `*${card.meta}*`);
        if (card.body) lines.push('', card.body);
      }
    }
  }
  return `${lines.join('\n')}\n`;
}

export function downloadMarkdown(filename: string, markdown: string): void {
  const blob = new Blob([markdown], { type: 'text/markdown;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(url);
}

// ─── HTML / print renderer ───────────────────────────────────────────────────

const escapeHtml = (text: string) =>
  text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

const PRINT_CSS = `
  body { font-family: Georgia, 'Times New Roman', serif; color: #111; margin: 24px; }
  h1 { font-size: 24px; margin: 0 0 2px; }
  h2 { font-size: 16px; border-bottom: 1px solid #999; padding-bottom: 2px; margin: 18px 0 8px; }
  h3 { font-size: 13px; margin: 0 0 1px; }
  .subtitle { color: #555; font-style: italic; margin: 0 0 8px; }
  .facts { font-size: 12px; margin: 0 0 4px; }
  .members { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; }
  .member { border: 1px solid #bbb; border-radius: 4px; padding: 8px 10px; margin: 0; page-break-inside: avoid; break-inside: avoid; overflow: hidden; }
  .member .role { color: #555; font-size: 11px; margin: 0 0 4px; }
  .member img { float: right; width: 72px; height: 72px; object-fit: cover; border-radius: 4px; margin-left: 8px; }
  .group-title { font-size: 14px; font-weight: 700; margin: 10px 0 6px; }
  table { border-collapse: collapse; font-size: 12px; margin: 4px 0; }
  th, td { border: 1px solid #999; padding: 2px 8px; text-align: left; }
  th { background: #eee; }
  /* Blank cells (e.g. experience tracker tallies) get write-in space. */
  td:empty { min-width: 80px; height: 20px; }
  ul { margin: 4px 0; padding-left: 18px; font-size: 12px; }
  p { font-size: 12px; margin: 4px 0; }
  .detail { font-size: 11px; margin: 2px 0; }
  .detail b { font-weight: 700; }
  .print-button { position: fixed; top: 12px; right: 12px; font-size: 14px; padding: 6px 16px; cursor: pointer; }
  @media print {
    .print-button { display: none; }
    /* Each major section starts on a fresh page. */
    .section { break-before: page; page-break-before: always; }
    .section:first-of-type { break-before: auto; page-break-before: auto; }
  }
  @page { margin: 15mm; }
`;

export function renderExportHtml(model: ExportModel, portraits: Map<string, string>): string {
  const statsTable = (stats: Array<[string, string]>) =>
    `<table><tr>${stats.map(([label]) => `<th>${escapeHtml(label)}</th>`).join('')}</tr>` +
    `<tr>${stats.map(([, value]) => `<td>${escapeHtml(value)}</td>`).join('')}</tr></table>`;
  const sections = model.sections.map((section) => {
    const parts: string[] = [`<h2>${escapeHtml(section.title)}</h2>`];
    const memberCards: string[] = [];
    for (const paragraph of section.paragraphs ?? []) parts.push(`<p>${escapeHtml(paragraph)}</p>`);
    if (section.bullets && section.bullets.length > 0) {
      parts.push(`<ul>${section.bullets.map((bullet) => `<li>${escapeHtml(bullet)}</li>`).join('')}</ul>`);
    }
    if (section.table) {
      parts.push(
        `<table><tr>${section.table.headers.map((header) => `<th>${escapeHtml(header)}</th>`).join('')}</tr>` +
        section.table.rows.map((row) => `<tr>${row.map((cell) => `<td>${escapeHtml(cell)}</td>`).join('')}</tr>`).join('') +
        '</table>'
      );
    }
    for (const member of section.members ?? []) {
      const portrait = portraits.get(member.memberId);
      memberCards.push(
        `<div class="member">${portrait ? `<img src="${portrait}" alt="" />` : ''}` +
        `<h3>${escapeHtml(member.name)}</h3><div class="role">${escapeHtml(member.subtitle)}</div>` +
        statsTable(member.stats) +
        member.details.map((detail) => `<div class="detail"><b>${escapeHtml(detail.label)}:</b> ${escapeHtml(detail.value)}</div>`).join('') +
        '</div>'
      );
    }
    for (const card of section.cards ?? []) {
      memberCards.push(
        `<div class="member"><h3>${escapeHtml(card.title)}</h3>` +
        (card.meta ? `<div class="role">${escapeHtml(card.meta)}</div>` : '') +
        (card.body ? `<p>${escapeHtml(card.body)}</p>` : '') +
        '</div>'
      );
    }
    if (memberCards.length > 0) parts.push(`<div class="members">${memberCards.join('')}</div>`);
    for (const group of section.cardGroups ?? []) {
      parts.push(`<div class="group-title">${escapeHtml(group.label)}</div>`);
      parts.push(`<div class="members">${group.cards.map((card) =>
        `<div class="member"><h3>${escapeHtml(card.title)}</h3>` +
        (card.meta ? `<div class="role">${escapeHtml(card.meta)}</div>` : '') +
        (card.body ? `<p>${escapeHtml(card.body)}</p>` : '') +
        '</div>').join('')}</div>`);
    }
    return `<section class="section">${parts.join('')}</section>`;
  }).join('');
  return `<!DOCTYPE html><html><head><meta charset="utf-8" /><title>${escapeHtml(model.title)}</title>` +
    `<style>${PRINT_CSS}</style></head><body>` +
    '<button class="print-button" onclick="window.print()">Print</button>' +
    `<h1>${escapeHtml(model.title)}</h1>` +
    (model.subtitle ? `<div class="subtitle">${escapeHtml(model.subtitle)}</div>` : '') +
    (model.facts.length > 0 ? `<div class="facts">${model.facts.map(([label, value]) => `<b>${escapeHtml(label)}:</b> ${escapeHtml(value)}`).join(' · ')}</div>` : '') +
    sections +
    '</body></html>';
}

/** Portraits for all roster members, as data URIs (PDF/print export only). */
export async function collectPortraits(warbandId: string, model: ExportModel): Promise<Map<string, string>> {
  const memberIds = model.sections.flatMap((section) => (section.members ?? []).map((member) => member.memberId));
  const portraits = new Map<string, string>();
  await Promise.all(memberIds.map(async (memberId) => {
    try {
      const blob = await warbandMemberImageService.fetchImage(warbandId, memberId);
      if (!blob) return;
      const dataUri = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result));
        reader.onerror = () => reject(reader.error);
        reader.readAsDataURL(blob);
      });
      portraits.set(memberId, dataUri);
    } catch {
      // Portraits are best-effort; export continues without them.
    }
  }));
  return portraits;
}

/** Opens the printable display in a new tab; printing is left to the user (via the Print button or Ctrl+P). */
export function openExportPreview(html: string): void {
  const previewWindow = window.open('', '_blank');
  if (!previewWindow) throw new Error('Allow pop-ups for this site to open the printable display.');
  previewWindow.document.write(html);
  previewWindow.document.close();
  previewWindow.focus();
}

export const sanitizeFilename = (name: string) =>
  name.replace(/[^a-z0-9\-_ ]/gi, '').trim().replace(/\s+/g, '-') || 'warband';
